import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useCurrentOrg } from '../orgs/useCurrentOrg';
import {
  listOrgSmartAccounts,
  listWallets,
  type SmartAccountListItem,
  type WalletListItem,
} from './api';
import { shortenAddress } from './format';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { StatusBadge } from './WalletBadges';

// Three-column view: EOAs / TEEs / Smart Accounts. The grouping reflects
// the EOA/TEE/SA refactor — each wallet's signing_mode picks between
// columns 1 and 2, and the wallet_smart_accounts table powers column 3
// (one row per (wallet, chain)).
//
// All three columns are scoped to the current org. Pagination is intentionally
// dropped from this view (we cap each fetch at 200 client-side) because the
// 3-column layout works poorly with per-column paginators; we'll add a
// "show all" detail view in a follow-up if any tenant outgrows that.

export function WalletsPage() {
  const { org } = useCurrentOrg();
  const orgId = org?.id ?? '';

  const wallets = useQuery({
    queryKey: ['wallets', orgId],
    queryFn: () => listWallets(orgId, { limit: 200, offset: 0 }),
    enabled: !!orgId,
    staleTime: 30_000,
  });

  const smartAccounts = useQuery({
    queryKey: ['smart-accounts', orgId],
    queryFn: () => listOrgSmartAccounts(orgId),
    enabled: !!orgId,
    staleTime: 30_000,
  });

  if (!org) return null;

  const items = wallets.data?.items ?? [];
  const eoas = items.filter((w) => (w.signing_mode ?? 'client') === 'client');
  const tees = items.filter((w) => w.signing_mode === 'tee');
  const sas = smartAccounts.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">Wallets</h2>
        <p className="mt-1 text-sm text-ink-secondary">
          End-user wallets, grouped by signing mode. Smart accounts opt-in on top of any EOA via the SDK.
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Column
          title="EOAs"
          subtitle="Client-side SSS — keys reconstructed in the browser"
          count={eoas.length}
          isLoading={wallets.isLoading}
          error={wallets.error}
          empty="No client wallets yet."
        >
          {eoas.map((w) => (
            <WalletItem key={w.id} w={w} />
          ))}
        </Column>

        <Column
          title="TEEs"
          subtitle="Server-side signing in a confidential VM"
          count={tees.length}
          isLoading={wallets.isLoading}
          error={wallets.error}
          empty="No TEE wallets yet."
        >
          {tees.map((w) => (
            <WalletItem key={w.id} w={w} />
          ))}
        </Column>

        <Column
          title="Smart Accounts"
          subtitle="ERC-4337 LightAccount v2 — gasless, per-chain"
          count={sas.length}
          isLoading={smartAccounts.isLoading}
          error={smartAccounts.error}
          empty="No smart accounts yet."
        >
          {sas.map((sa) => (
            <SmartAccountItem key={sa.id} sa={sa} />
          ))}
        </Column>
      </div>
    </div>
  );
}

function Column({
  title,
  subtitle,
  count,
  isLoading,
  error,
  empty,
  children,
}: {
  title: string;
  subtitle: string;
  count: number;
  isLoading: boolean;
  error: unknown;
  empty: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader
        title={title}
        description={
          isLoading ? 'Loading…' : `${count} ${count === 1 ? 'item' : 'items'} — ${subtitle}`
        }
      />
      <CardBody className="!p-0">
        {error ? (
          <p className="px-6 py-5 text-sm text-bad">
            {error instanceof Error ? error.message : 'Could not load'}
          </p>
        ) : !isLoading && count === 0 ? (
          <p className="px-6 py-10 text-center text-sm text-ink-tertiary">{empty}</p>
        ) : (
          <ul className="divide-y divide-line-soft">{children}</ul>
        )}
      </CardBody>
    </Card>
  );
}

function WalletItem({ w }: { w: WalletListItem }) {
  const curve = w.curve || 'secp256k1';
  const networks = w.networks?.join(', ') ?? w.chain_type;
  const owner = w.email || w.provider_user_id;
  return (
    <li className="px-5 py-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-ink-primary">{owner}</p>
          <p className="font-mono text-[11px] text-ink-tertiary">
            {curve} · {networks}
          </p>
        </div>
        <StatusBadge status={w.status} />
      </div>
      <Link
        to={`/wallets/${w.id}`}
        className="mt-1 block break-all font-mono text-[11px] text-ink-secondary hover:text-bit2me"
        title={w.address}
      >
        {shortenAddress(w.address)}
      </Link>
    </li>
  );
}

function SmartAccountItem({ sa }: { sa: SmartAccountListItem }) {
  return (
    <li className="px-5 py-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-mono text-[11px] text-ink-primary">
            {sa.chain} · {sa.factory}
          </p>
          <p className="mt-0.5 font-mono text-[10px] text-ink-tertiary">
            owner {shortenAddress(sa.owner_address)} · created by {sa.created_by}
          </p>
        </div>
        <DeployedBadge deployed={sa.deployed} />
      </div>
      <Link
        to={`/wallets/${sa.wallet_id}`}
        className="mt-1 block break-all font-mono text-[11px] text-ink-secondary hover:text-bit2me"
        title={sa.address}
      >
        {shortenAddress(sa.address)}
      </Link>
    </li>
  );
}

function DeployedBadge({ deployed }: { deployed: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-eyebrow ${
        deployed ? 'text-ok' : 'text-ink-tertiary'
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${deployed ? 'bg-ok' : 'bg-ink-muted'}`} />
      {deployed ? 'deployed' : 'counterfactual'}
    </span>
  );
}
